import { Controller } from "@hotwired/stimulus"

// The staff rail's collapse switch, after UYV-Time's sidebar controller.
// Flips data-rail on <body> (CSS turns that into --rail-w, which moves the
// rail and the page column together) and saves it in the rail cookie, so the
// server paints the same state on the next page with no flash.
//
// It also opens and closes a rail item's sub menu (Settings): data-submenu
// on this element slides the panel out from under the rail (inline below
// md). With a mouse on a wide screen, hovering the item opens it and moving
// off both the item and the panel closes it, after a short grace so the
// pointer can cross from one to the other. A click opens it too (keyboard,
// touch). A choice in it, Escape or a click outside slides it back in.
// From the keyboard, opening moves focus to the panel's first link (the
// panel sits after the whole rail in tab order), and Escape inside the panel
// hands focus back to the rail item.
export default class extends Controller {
  static targets = ["toggle", "submenuButton", "flyout"]

  connect() {
    this.hoverable = window.matchMedia("(hover: hover) and (min-width: 768px)")
    this.wide = window.matchMedia("(min-width: 768px)")
    this.syncToggle()
    this.onOutside = (event) => {
      if (!this.element.contains(event.target)) this.closeSubmenu()
    }
    this.onKey = (event) => {
      if (event.key !== "Escape" || this.element.dataset.submenu !== "open") return
      const inFlyout = this.hasFlyoutTarget && this.flyoutTarget.contains(document.activeElement)
      this.closeSubmenu()
      if (inFlyout) this.submenuButtonTargets[0]?.focus()
    }
    document.addEventListener("click", this.onOutside)
    document.addEventListener("keydown", this.onKey)
  }

  disconnect() {
    clearTimeout(this.hoverTimer)
    document.removeEventListener("click", this.onOutside)
    document.removeEventListener("keydown", this.onKey)
  }

  // A click while hover already opened it keeps it open, rather than
  // closing it under the pointer.
  toggleSubmenu(event) {
    if (this.hoverable.matches) {
      this.setSubmenu(true)
    } else {
      this.setSubmenu(this.element.dataset.submenu !== "open")
    }
    // detail is 0 for a click made with Enter or Space. Below md the links
    // are an inline list right after the button, so Tab already reaches them.
    if (event?.detail === 0 && this.wide.matches && this.element.dataset.submenu === "open" && this.hasFlyoutTarget) {
      requestAnimationFrame(() => this.flyoutTarget.querySelector("a")?.focus())
    }
  }

  hoverOpen() {
    if (!this.hoverable.matches) return
    clearTimeout(this.hoverTimer)
    this.setSubmenu(true)
  }

  hoverClose() {
    if (!this.hoverable.matches) return
    clearTimeout(this.hoverTimer)
    this.hoverTimer = setTimeout(() => this.closeSubmenu(), 150)
  }

  closeSubmenu() {
    this.setSubmenu(false)
  }

  setSubmenu(open) {
    if (open) {
      this.element.dataset.submenu = "open"
    } else {
      delete this.element.dataset.submenu
    }
    this.submenuButtonTargets.forEach((button) => button.setAttribute("aria-expanded", String(open)))
  }

  toggle() {
    const collapsed = document.body.dataset.rail !== "collapsed"
    if (collapsed) {
      document.body.dataset.rail = "collapsed"
    } else {
      delete document.body.dataset.rail
    }
    document.cookie = `rail=${collapsed ? "collapsed" : "open"}; path=/; max-age=31536000; samesite=lax`
    this.syncToggle()
  }

  syncToggle() {
    if (!this.hasToggleTarget) return
    const collapsed = document.body.dataset.rail === "collapsed"
    this.toggleTarget.setAttribute("aria-expanded", String(!collapsed))
    this.toggleTarget.setAttribute("aria-label", collapsed ? "Expand menu" : "Collapse menu")
  }
}
